import { useState, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Box } from 'src/viewLayer/components/DesignSystem';
import { Loader } from 'src/viewLayer/components/shared/Loader';
import { Navbar } from 'src/viewLayer/components/Navbar';
import { getFlowProgress, goToNextNode } from 'src/viewLayer/components/Flow/flowHelpers';
import { LOCALE } from 'src/dataLayer/env';
import useUserInput from 'src/viewLayer/components/hooks/useUserInput';
import availableNodes from 'src/viewLayer/components/nodes';
import { useFlowHooks } from 'src/viewLayer/components/hooks/flowHooks';
import { UserDataType } from 'src/viewLayer/components/nodes/types.common';
import type { FlowConfig, NodeType } from './types';

export const Flow = (): JSX.Element => {
  const navigate = useNavigate();
  // ATTENTION: do not use `locale` from params, we use it from data-env as `LOCALE`
  const { nodeNumber: nodeNumberFromParams, flowId, environment, learnLang } = useParams();
  const nodeNumber = Number.parseInt(nodeNumberFromParams, 10);

  // State
  const [queryParams, setQueryParams] = useSearchParams();
  const [flowConfig, setFlowConfig] = useState<FlowConfig>();
  // userInput is the flow level state for validated input the user has submitted during registration
  const [userInput, setUserInputWithLocalStorage] = useUserInput({});
  // userData is set once a user has registered
  const [userData, setUserData] = useState<UserDataType>();

  // routeConfig needs to be a ref so that the useEffect for redirection doesn't end up in an endless update loop
  const routeConfig = useRef({
    flowId,
    nodeNumber,
    locale: LOCALE,
    environment,
    learnLang,
    queryParams
  });

  useFlowHooks({
    queryParams,
    setQueryParams,
    learnLang,
    routeConfig,
    navigate,
    flowConfig,
    setFlowConfig,
    flowId,
    nodeNumber
  });

  const flow = flowConfig?.flow;
  if (!flow || !(nodeNumber < flow?.length)) return <Loader/>;

  const currentNodeConfig = flow[nodeNumber];
  const currentNodeType = currentNodeConfig.type as NodeType;
  const CurrentNode = availableNodes[currentNodeType];
  const showProgressBar = flowConfig?.options?.showProgressBar;

  return (
    <Box data-testid="flow-container" className="FlowContainer">
      <Navbar
        hideLoginButton={ currentNodeConfig?.options?.hideLoginButton }
        progressData={ showProgressBar ? getFlowProgress(flow.length, nodeNumber) : undefined }
        showProgressBar={ showProgressBar }
        learnLang={ learnLang }
        signinToken={ userData?.signin_token }
      />
      <CurrentNode
        nodeConfig={ currentNodeConfig }
        goToNextNode={ (): void => goToNextNode({ environment, learnLang, flowId, nodeNumber, queryParams, navigate }) }
        emailVerificationTarget={ flowConfig?.options?.emailVerificationTarget }
        userInput={ userInput }
        setUserInput={ setUserInputWithLocalStorage }
        userData={ userData }
        setUserData={ setUserData }
        trackingInfo={ { flowId, flowVersion: flowConfig.version } }
      />
    </Box>
  );
};
